import type { CachedCaster } from './types';
import { sheetDefinition, k5Environment } from './k5-runtime';
import { calculateDamageDice, calculateDamageMod } from '../janus-engine/port/k5-render/ability_effect_speed_renderers';
import { abilityEntryId, abilityEntryCharges, abilityEntryMaxCharges, passiveActive, uuidOf, catalogNameById } from './tabs/ability-row';
import { sheetChargeBudget, spellIsLevelUpcastable } from './ability-button';
import { num } from './read-helpers';
import { at, values } from '../janus-engine/port/k5-render/support';

type Row = Record<string, unknown>;
const entries = (row: unknown, name: string): unknown[] => ftLayerEntries(row, name) ?? [];
const value = (row: unknown, name: string): unknown => ftLayerValue(row, name);
const field = (row: unknown, name: string): unknown => ftItemsCol(row, name);

/** An option's name from the caster's compiled castable rows (`computed.creature.castable`), which
 *  carry each choice pre-named even when the option's own definition is held nowhere. */
function compiledChoiceName(caster: CachedCaster, id: string): string {
    for (const rows of values(at(caster, 'computed', 'creature', 'castable')))
        for (const row of values(rows))
            for (const key of ['summon_choices', 'shapeshift_choices', 'ability_choices'])
                for (const choice of values(at(row, key)))
                    if (String(at(choice, 'id') ?? '').toUpperCase() === id.toUpperCase()) return String(at(choice, 'name') ?? '');
    return '';
}

export function sheetChoices(caster: CachedCaster, ability: unknown, layer: string): Array<{ id: string; name: string }> | null {
    const pool = entries(ability, layer);
    if (!pool.length) return null;
    return pool.map(entry => {
        const id = uuidOf(entry);
        try {
            const def = sheetDefinition(caster, entry);
            return { id: String(field(def, 'item_id') ?? id), name: String(field(def, 'item_name') ?? '') };
        } catch {
            // A missing branch affects this option alone. Its selected definition
            // is loaded by the ordinary object read before a button is painted;
            // the label only needs a held identity (a summon's catalog row) or the compiled name.
            const name = catalogNameById(id) || compiledChoiceName(caster, id);
            return { id, name: name || `unported:creature-display:definition-not-held:${id}` };
        }
    });
}

/** Lists and effects come from the browser's creature array, including lean refs.
 * Missing definitions surface as refusals; empty/omitted lists mean no entries. */
export function sheetCastablePayload(caster: CachedCaster): Row {
    const env = k5Environment();
    const result: Row = { can_edit: typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster) };
    for (const list of ['spells_known', 'curses_known', 'at_will_spell', 'innate_spell_per_long_rest', 'charged_spell']) {
        // NPC preparation is already curated by the same native compiler as
        // encounter creatures. Unlike PCs, NPCs have no separate spells_known list.
        const bakedKnown = at(caster, 'computed', 'creature', 'castable', 'spells_known');
        const spellEntries = list === 'spells_known' && bakedKnown !== null
            && at(caster, 'computed', 'creature', 'spells_known') === null
            ? values(bakedKnown) : entries(caster, list);
        result[list] = spellEntries.map(entry => {
            const def = sheetDefinition(caster, entry);
            const source = at(entry, 'source') ?? null;
            const findInventory = (rows: unknown[]): unknown => {
                for (const row of rows) {
                    if (String(ftInventoryField(row, 'inventory_id') ?? '') === source) return row;
                    const child = findInventory(values(at(row, 'inventory')));
                    if (child) return child;
                }
                return null;
            };
            const inventory = list === 'charged_spell' ? findInventory(entries(caster, 'inventory')) : null;
            // The existing row hydrator supplies range/time/icons from this full
            // definition. Only caster-dependent overrides need a separate field.
            return {
                item_id: String(field(def, 'item_id') ?? abilityEntryId(entry)), nest: def,
                spell_type: ['spells_known', 'curses_known'].includes(list) ? null : list,
                source,
                charges: list === 'innate_spell_per_long_rest' ? { current: abilityEntryCharges(entry, 0), max: abilityEntryMaxCharges(entry, 1) } : null,
                inventory_charges: list === 'charged_spell' && inventory ? { current: num(value(inventory, 'current_item_charges')),
                    max: num(value(inventory, 'item_charges')), source_icon_type: String(field(inventory, 'item_type') ?? '') } : null,
                effect: ftLayerHas(def, 'die') ? {
                    dice: calculateDamageDice(value(def, 'dice'), def, caster, env),
                    mod: calculateDamageMod(def, caster, env),
                } : null,
                summon_choices: sheetChoices(caster, def, 'user_choice_1_summon'),
                shapeshift_choices: sheetChoices(caster, def, 'user_choice_1_shapeshift'),
                ability_choices: sheetChoices(caster, def, 'ability_choice'),
            };
        });
    }
    return result;
}

export function sheetMentasPayload(caster: CachedCaster): Row {
    const mentas = entries(caster, 'menta').map(entry => {
        const nest = sheetDefinition(caster, entry), id = String(field(nest, 'item_id') ?? abilityEntryId(entry));
        const level = num(value(nest, 'menta_level'));
        let mod = value(nest, 'mod') ?? 0;
        if (String(mod).includes('[spellcasting_essence]')) mod = value(caster, 'spellcasting_essence_mod') ?? 0;
        if (String(mod).includes('[menta_level]')) mod = level;
        const roll = String(value(nest, 'roll_type') ?? '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0');
        const healing = ['4FB7452A-F130-40C3-83E6-5E92C329D7B2', '5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591', '4634635E-4E82-DFB3-8411-27A6BFB8CF0F'].includes(roll);
        const pool = entries(caster, 'passive_ability').find(entry => passiveActive(entry)
            && abilityEntryId(entry) === value(nest, 'consumes_ability_charges'));
        // The [x] stepper's bound is the shared investment budget (a protected holder → the pool max), as on the abilities tab.
        const charges = pool ? sheetChargeBudget(caster, pool) : '';
        return { nest, id, name: String(field(nest, 'item_name') ?? ''), level,
            concentration: ftLayerHas(nest, 'spell_concentration'),
            effect: ftLayerHas(nest, 'die') ? { dice: value(nest, 'dice'), die: value(nest, 'die'), mod,
                roll_type_id: roll, damage_type: healing ? 'healing' : value(nest, 'damage_type') ?? '',
                has_damage_type_icon: ftLayerHas(nest, 'damage_type'), is_healing: healing } : null,
            summon_choices: sheetChoices(caster, nest, 'user_choice_1_summon'),
            ability_choices: sheetChoices(caster, nest, 'ability_choice'),
            upcast: spellIsLevelUpcastable(nest) ? { kind: 'level', min: level, max: '', value: level }
                : value(nest, 'consumes_charges_qty') === '[x]' && ftLayerHas(nest, 'consumes_ability_charges')
                    ? { kind: 'variable', min: num(charges) === 0 ? 0 : 1, max: charges, value: charges } : null,
        };
    }).sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
    return { mentas, max_menta_level_castable: Math.max(0, ...mentas.map(m => m.level)),
        can_edit: typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster) };
}
